import React, { useState, useEffect, useCallback } from "react";
import { Card, Empty, Space, Row, Col, Alert, Spin, Typography, Tag, Collapse, Button } from "antd";
import { useNavigate } from "react-router-dom";
import { useAutoRequestViewContext } from "../../hooks/useAutoRequestView";
import PageHeader from "../../components/common/PageHeader";
import consultaWeb from "../../services/consultaWeb";
import { calculateStatusCounts, StatusCounts } from "../../utils/statusCounts";
import SourceViewer from "../../components/SourceViewer";
import { SourceProvider } from "../../hooks/useSource";
import { CheckCircleOutlined, CloseCircleOutlined, ArrowLeftOutlined } from "@ant-design/icons";

const { Text } = Typography;
const { Panel } = Collapse;

interface AutoRequestData {
  id: string;
  data: any;
  requests: string[]; // IDs das requests
  loading: boolean;
  error: string | null;
  notFound: boolean;
  stats?: StatusCounts;
}

/**
 * Componente para visualizar um autorequest individual e suas requests.
 */
const AutoRequestViewer: React.FC<{ autoRequestId: string }> = ({ autoRequestId }) => {
  const [autoRequestData, setAutoRequestData] = useState<AutoRequestData | null>(null);
  const [loading, setLoading] = useState<boolean>(true);

  /**
   * Carrega os dados do autorequest.
   */
  const loadAutoRequest = useCallback(async () => {
    try {
      setLoading(true);
      const response = await consultaWeb.getAutoRequestAdmin({ idAutoRequest: autoRequestId });

      // Verifica se não foi encontrado
      if (!response.success || response.code === "NOT_FOUND" || !response.data) {
        setAutoRequestData({
          id: autoRequestId,
          data: null,
          requests: [],
          loading: false,
          error: response.message || "Auto request não encontrado",
          notFound: true,
          stats: {
            pending: 0,
            warning: 0,
            success: 0,
            error: 0,
            total: 0,
          },
        });
        return;
      }

      // Extrai os IDs das requests do autorequest
      // A estrutura retornada pelo autoRequestFormat() tem:
      // { id, key, value, requests: [{ id, status, source, platform, ... }, ...] }
      const requests: string[] = [];
      
      if (response.data && response.data.requests && Array.isArray(response.data.requests)) {
        response.data.requests.forEach((req: any) => {
          if (req && req.id) {
            requests.push(req.id);
          }
        });
      }

      // Calcula estatísticas a partir das requests retornadas
      const stats = calculateStatusCounts(response.data.requests, (r: any) => r.status);

      setAutoRequestData({
        id: autoRequestId,
        data: response.data,
        requests,
        loading: false,
        error: null,
        notFound: false,
        stats,
      });
    } catch (err: any) {
      console.error("Erro ao buscar autorequest:", err);
      setAutoRequestData({
        id: autoRequestId,
        data: null,
        requests: [],
        loading: false,
        error: err.response?.data?.message || err.message || "Erro ao buscar autorequest",
        notFound: false,
        stats: {
          pending: 0,
          warning: 0,
          success: 0,
          error: 0,
          total: 0,
        },
      });
    } finally {
      setLoading(false);
    }
  }, [autoRequestId]);

  useEffect(() => {
    loadAutoRequest();
  }, [loadAutoRequest]);

  if (loading) {
    return (
      <Collapse>
      <Panel
          header={
            <div style={{ display: "flex", alignItems: "center", gap: "12px", flexWrap: "wrap" }}>
              <Text copyable code style={{ fontSize: "14px" }}>
                {autoRequestId}
              </Text>
              <Spin size="small" />
            </div>
          }
          key={autoRequestId}
          collapsible="disabled"
        >
          {/* Conteúdo vazio durante loading (painel não colapsável) */}
        </Panel>
      </Collapse>
    );
  }

  if (!autoRequestData) {
    return null;
  }

  if (autoRequestData.notFound || autoRequestData.error) {
    return (
      <Collapse>
        <Panel
          header={
            <div style={{ display: "flex", alignItems: "center", gap: "12px", flexWrap: "wrap" }}>
              <Text copyable code style={{ fontSize: "14px" }}>
                {autoRequestId}
              </Text>
              <Tag color="error" icon={<CloseCircleOutlined />}>
                {autoRequestData.notFound ? "Não encontrado" : "Erro"}
              </Tag>
            </div>
          }
          key={autoRequestId}
          collapsible="disabled"
        >
          {/* Conteúdo vazio para autorequests não encontrados (painel não colapsável) */}
        </Panel>
      </Collapse>
    );
  }

  const autoRequest = autoRequestData.data?.requests[0];
  return (
    <Collapse>
      <Panel
        header={
          <div style={{ display: "flex", alignItems: "center", gap: "12px", flexWrap: "wrap" }}>
            <Text copyable code style={{ fontSize: "14px" }}>
              {autoRequestId}
            </Text>
            {autoRequest && (
              <div style={{ display: "flex", gap: 2, alignItems: "center", flexWrap: "wrap" }}>
                {/* <Tag color="cyan" style={{ fontSize: "12px", marginRight: 4, padding: "0 6px" }}>
                  {autoRequest.platform || "-"}
                </Tag>
                <Tag color="volcano" style={{ fontSize: "12px", marginRight: 4, padding: "0 6px" }}>
                  {autoRequest.search || "-"}
                </Tag>
                <Tag color="green" style={{ fontSize: "12px", marginRight: 4, padding: "0 6px" }}>
                  {autoRequest.query ?? "-"}
                </Tag>
                <Tag color="geekblue" style={{ fontSize: "12px", marginRight: 4, padding: "0 6px" }}>
                  {String(autoRequest.param.key ?? "-")}
                </Tag> */}
                <Tag color="purple" style={{ fontSize: "12px", marginRight: 4, padding: "0 6px" }}>
                  {String(autoRequest.param.value ?? "-")}
                </Tag>
              </div>
            )}
            <Tag color="success" icon={<CheckCircleOutlined />}>
              Encontrado
            </Tag>
            <div style={{ display: "flex", alignItems: "center", marginLeft: "auto", gap: 6 }}>
              {autoRequestData.stats && (
                <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                  <Tag color="processing" style={{ fontWeight: 600 }}>
                    {autoRequestData.stats.pending}
                  </Tag>
                  <Tag color="success" style={{ fontWeight: 600 }}>
                    {autoRequestData.stats.success}
                  </Tag>
                  <Tag color="error" style={{ fontWeight: 600 }}>
                    {autoRequestData.stats.error}
                  </Tag>
                  {autoRequestData.stats.warning > 0 && (
                    <Tag color="warning" style={{ fontWeight: 600 }}>
                      {autoRequestData.stats.warning}
                    </Tag>
                  )}
                </div>
              )}
              {autoRequestData.requests.length > 0 && (
                <Tag color="default" style={{ marginLeft: 4 }}>
                  {autoRequestData.requests.length} request(s)
                </Tag>
              )}
            </div>
          </div>
        }
        key={autoRequestId}
      >
        <Space direction="vertical" size="large" style={{ width: "100%" }}>
          {autoRequestData.requests.length === 0 ? (
            <Alert
              message="Nenhuma request encontrada neste autorequest"
              type="info"
              showIcon
            />
          ) : (
            <Row gutter={[16, 16]}>
              {autoRequestData.requests.map((requestId, idx) => (
                <Col xs={24} sm={24} md={12} lg={12} xl={12} key={idx}>
                  <SourceViewer sourceId={requestId} />
                </Col>
              ))}
            </Row>
          )}
        </Space>
      </Panel>
    </Collapse>
  );
};

/**
 * Página de monitoramento de autorequests.
 *
 * Exibe todos os autorequests que estão sendo buscados e suas requests associadas.
 */
const AutoRequestViewMonitoringContent: React.FC = () => {
  const { autoRequestIds } = useAutoRequestViewContext();
  const navigate = useNavigate();

  const handleBack = () => {
    navigate("/auto-request/buscar");
  };

  return (
    <Card>
      <Space direction="vertical" size="large" style={{ width: "100%" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <PageHeader title="Monitoramento de Autorequests" />
          <Button icon={<ArrowLeftOutlined />} onClick={handleBack}>
            Voltar
          </Button>
        </div>

        {autoRequestIds.length === 0 ? (
          <Empty 
            description="Nenhum autorequest sendo monitorado. Volte para a página de Buscar por Autorequest para adicionar." 
            image={Empty.PRESENTED_IMAGE_SIMPLE} 
          />
        ) : (
          <Row gutter={[16, 16]}>
            {autoRequestIds.map((autoRequestId, idx) => (
              <Col xs={24} sm={24} md={24} lg={24} xl={24} key={idx}>
                <AutoRequestViewer autoRequestId={autoRequestId} />
              </Col>
            ))}
          </Row>
        )}
      </Space>
    </Card>
  );
};

/**
 * Componente principal da página AutoRequestViewMonitoring.
 */
const AutoRequestViewMonitoring: React.FC = () => {
  return (
    <AutoRequestViewMonitoringContent />
  );
};

export default AutoRequestViewMonitoring;
